import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  useTheme,
  alpha,
  Chip,
  Stack,
  Paper,
  Divider,
  IconButton,
  Tooltip as MuiTooltip
} from '@mui/material';
import {
  TrendingUp,
  AttachMoney,
  AccountBalance,
  PieChart,
  BarChart,
  CheckCircle,
  Cancel,
  Info
} from '@mui/icons-material';
import Loader from '../../../components/layout/Loader';
import { API_URI } from '../../../constants/GlobalConstants';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const ProductPaymentAnalytics = () => {
  const [analyticsData, setAnalyticsData] = useState({});
  const [loading, setLoading] = useState(true);
  const theme = useTheme();

  const userLogin = useSelector((state) => state.userSignIn);
  const { userInfo } = userLogin;

  useEffect(() => {
    const fetchAnalyticsData = async () => {
      try {
        const config = {
          headers: {
            Authorization: `Bearer ${userInfo.token}`,
          },
        };
        const { data } = await axios.get(`${API_URI}/api/analytics/product-payment-analytics`, config);
        setAnalyticsData(data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching product payment analytics', error);
        setLoading(false);
      }
    };
    fetchAnalyticsData();
  }, [userInfo.token]);

  const modernColors = [
    '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', 
    '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E9'
  ];

  const paymentColors = {
    paid: '#28a745',
    unpaid: '#dc3545'
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: '#ffffff',
          font: {
            size: 12,
            family: 'Roboto, sans-serif',
            weight: '500'
          },
          padding: 20,
          usePointStyle: true,
          pointStyle: 'circle'
        }
      },
      tooltip: {
        backgroundColor: 'rgba(33, 37, 41, 0.95)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: theme.palette.primary.main,
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
        titleFont: {
          size: 14,
          weight: 'bold'
        },
        bodyFont: {
          size: 13
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          color: '#ffffff',
          callback: value => `$${value.toLocaleString()}`,
          font: {
            size: 11
          }
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.1)',
          lineWidth: 1
        },
        border: {
          color: 'rgba(255, 255, 255, 0.2)'
        }
      },
      x: {
        ticks: {
          color: '#ffffff',
          font: {
            size: 11
          },
          maxRotation: 45
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.1)',
          lineWidth: 1
        },
        border: {
          color: 'rgba(255, 255, 255, 0.2)'
        }
      }
    }
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
        labels: {
          color: '#ffffff',
          font: {
            size: 13,
            family: 'Roboto, sans-serif',
            weight: '500'
          },
          padding: 20,
          usePointStyle: true,
          pointStyle: 'circle'
        }
      },
      tooltip: {
        backgroundColor: 'rgba(33, 37, 41, 0.95)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: theme.palette.primary.main,
        borderWidth: 1,
        cornerRadius: 8,
        callbacks: {
          label: (tooltipItem) => {
            const label = tooltipItem.label || '';
            const value = tooltipItem.raw || 0;
            const total = tooltipItem.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return `${label}: ${value} orders (${percentage}%)`;
          }
        }
      }
    },
    cutout: '60%',
    elements: {
      arc: {
        borderWidth: 3,
        borderColor: '#1e1e1e'
      }
    }
  };

  const topProductsData = {
    labels: analyticsData.topProducts?.map(product => product.productType) || [],
    datasets: [
      {
        label: 'Revenue ($)',
        data: analyticsData.topProducts?.map(product => product.totalRevenue) || [],
        backgroundColor: modernColors.map(color => alpha(color, 0.8)),
        borderColor: modernColors,
        borderWidth: 2,
        borderRadius: 8,
        borderSkipped: false,
        hoverBackgroundColor: modernColors,
        hoverBorderWidth: 3
      }
    ]
  };

  const paymentStatusData = {
    labels: ['Paid Orders', 'Unpaid Orders'],
    datasets: [
      {
        data: [
          analyticsData.paymentStatus?.paid || 0,
          analyticsData.paymentStatus?.unpaid || 0,
        ],
        backgroundColor: [
          alpha(paymentColors.paid, 0.8),
          alpha(paymentColors.unpaid, 0.8)
        ],
        borderColor: [paymentColors.paid, paymentColors.unpaid],
        borderWidth: 3,
        hoverOffset: 8,
        hoverBackgroundColor: [paymentColors.paid, paymentColors.unpaid]
      }
    ]
  };

  // Calculate totals and percentages
  const totalOrders = (analyticsData.paymentStatus?.paid || 0) + (analyticsData.paymentStatus?.unpaid || 0);
  const totalRevenue = analyticsData.topProducts?.reduce((sum, product) => sum + product.totalRevenue, 0) || 0;
  const paidPercentage = totalOrders > 0 ? ((analyticsData.paymentStatus?.paid || 0) / totalOrders * 100).toFixed(1) : 0;

  const StatsCard = ({ title, value, subtitle, icon, color = 'primary', trend }) => (
    <Card
      elevation={3}
      sx={{
        height: '100%',
        background: `linear-gradient(135deg, ${alpha(theme.palette[color].main, 0.1)} 0%, ${alpha(theme.palette[color].main, 0.05)} 100%)`,
        border: `1px solid ${alpha(theme.palette[color].main, 0.2)}`,
        transition: 'all 0.3s ease-in-out',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: theme.shadows[8],
        }
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Box
            sx={{
              p: 2,
              borderRadius: '12px',
              backgroundColor: alpha(theme.palette[color].main, 0.15),
              color: theme.palette[color].main,
            }}
          >
            {icon}
          </Box>
          {trend && (
            <Chip
              label={trend}
              size="small"
              sx={{
                backgroundColor: alpha(theme.palette.success.main, 0.1),
                color: theme.palette.success.main,
                fontWeight: 'bold'
              }}
            />
          )}
        </Box>
        
        <Typography
          variant="h4"
          component="div"
          fontWeight="bold"
          className="text-white"
          sx={{ color: 'white !important', mb: 1 }}
        >
          {value}
        </Typography>
        
        <Typography
          variant="h6"
          className="text-white"
          sx={{ color: 'white !important', mb: 1 }}
        >
          {title}
        </Typography>
        
        {subtitle && (
          <Typography
            variant="body2"
            className="text-white-50"
            sx={{ color: 'rgba(255,255,255,0.7) !important' }}
          >
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  const ChartCard = ({ title, children, icon, description }) => (
    <Card
      elevation={3}
      sx={{
        height: '100%',
        backgroundColor: '#1e1e1e',
        border: '1px solid #333',
        transition: 'all 0.3s ease-in-out',
        '&:hover': {
          boxShadow: theme.shadows[8],
        }
      }}
    >
      <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
          <Box display="flex" alignItems="center">
            <Box
              sx={{
                p: 1.5,
                borderRadius: '10px',
                backgroundColor: alpha(theme.palette.primary.main, 0.15),
                color: theme.palette.primary.main,
                mr: 2,
              }}
            >
              {icon}
            </Box>
            <Box>
              <Typography
                variant="h6"
                fontWeight="bold"
                className="text-white"
                sx={{ color: 'white !important' }}
              >
                {title}
              </Typography>
              {description && (
                <Typography
                  variant="body2"
                  className="text-white-50"
                  sx={{ color: 'rgba(255,255,255,0.7) !important' }}
                >
                  {description}
                </Typography>
              )}
            </Box>
          </Box>
          
          <MuiTooltip title="Last 6 months data">
            <IconButton size="small" className="text-white-50">
              <Info fontSize="small" />
            </IconButton>
          </MuiTooltip>
        </Box>
        
        <Box flex={1}>
          {children}
        </Box>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Loader />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 0 }}>
      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Total Revenue"
            value={`$${totalRevenue.toLocaleString()}`}
            subtitle="Last 6 months"
            icon={<TrendingUp fontSize="large" />}
            color="success"
            trend="+12.5%"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Total Orders"
            value={totalOrders.toLocaleString()}
            subtitle="Paid & Unpaid"
            icon={<AccountBalance fontSize="large" />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Paid Orders"
            value={analyticsData.paymentStatus?.paid || 0}
            subtitle={`${paidPercentage}% of total`}
            icon={<CheckCircle fontSize="large" />}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Unpaid Orders"
            value={analyticsData.paymentStatus?.unpaid || 0}
            subtitle={`${(100 - paidPercentage).toFixed(1)}% of total`}
            icon={<Cancel fontSize="large" />}
            color="error"
          />
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={3}>
        <Grid item xs={12} lg={8}>
          <ChartCard
            title="Top Products by Revenue"
            description="Revenue performance of top 5 products"
            icon={<BarChart fontSize="large" />}
          >
            <Box sx={{ height: '400px' }}>
              <Bar data={topProductsData} options={chartOptions} />
            </Box>
          </ChartCard>
        </Grid>
        
        <Grid item xs={12} lg={4}>
          <ChartCard
            title="Payment Status Overview"
            description="Distribution of order payments"
            icon={<PieChart fontSize="large" />}
          >
            <Box sx={{ height: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Box sx={{ position: 'relative', width: '100%', height: '350px' }}>
                <Doughnut data={paymentStatusData} options={doughnutOptions} />
                
                {/* Center text for doughnut */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    textAlign: 'center',
                    pointerEvents: 'none'
                  }}
                >
                  <Typography
                    variant="h6"
                    className="text-white"
                    sx={{ color: 'white !important', fontWeight: 'bold' }}
                  >
                    {totalOrders}
                  </Typography>
                  <Typography
                    variant="body2"
                    className="text-white-50"
                    sx={{ color: 'rgba(255,255,255,0.7) !important' }}
                  >
                    Total Orders
                  </Typography>
                </Box>
              </Box>
            </Box>
          </ChartCard>
        </Grid>
      </Grid>

      {/* Additional Insights */}
      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid item xs={12}>
          <Paper
            elevation={2}
            sx={{
              p: 3,
              backgroundColor: '#1e1e1e',
              border: '1px solid #333'
            }}
          >
            <Typography
              variant="h6"
              className="text-white"
              sx={{ color: 'white !important', mb: 2 }}
            >
              Key Insights
            </Typography>
            
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <Chip
                icon={<TrendingUp />}
                label={`Payment Success Rate: ${paidPercentage}%`}
                sx={{
                  backgroundColor: alpha(theme.palette.success.main, 0.1),
                  color: theme.palette.success.main,
                  fontWeight: 'bold'
                }}
              />
              <Chip
                icon={<AttachMoney />}
                label={`Average Order Value: $${totalOrders > 0 ? (totalRevenue / totalOrders).toFixed(2) : '0'}`}
                sx={{
                  backgroundColor: alpha(theme.palette.info.main, 0.1),
                  color: theme.palette.info.main,
                  fontWeight: 'bold'
                }}
              />
              <Chip
                icon={<AccountBalance />}
                label={`Outstanding Amount: $${((analyticsData.paymentStatus?.unpaid || 0) * (totalRevenue / totalOrders || 0)).toFixed(2)}`}
                sx={{
                  backgroundColor: alpha(theme.palette.warning.main, 0.1),
                  color: theme.palette.warning.main,
                  fontWeight: 'bold'
                }}
              />
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ProductPaymentAnalytics;